import api from '@/lib/axios';

import type { PaginatedResponse } from '@/types/tenant';
import type {
  FormDataDto,
  SaveFormDataPayload,
  BulkFormDataPayload,
  FormDataVersionDto,
} from '@/types/formData';

// ============================================
// Helpers
// ============================================

const basePath = (objectType: string, objectId: string) =>
  `/form-service/v1/form-data/${objectType}/${objectId}`;

// ============================================
// Form Data — CRUD
// ============================================

/**
 * Save or update form submission data (upsert).
 * PUT /form-data/{objectType}/{objectId}/{formConfigId}
 * Returns 201 on create, 200 on update — both return FormDataDto.
 */
export const saveFormData = async (
  objectType: string,
  objectId: string,
  formConfigId: string,
  payload: SaveFormDataPayload,
): Promise<FormDataDto> => {
  const response = await api.put<FormDataDto>(
    `${basePath(objectType, objectId)}/${formConfigId}`,
    payload,
    { successToast: true },
  );
  return response.data;
};

/**
 * Get saved form data for a specific form config.
 * GET /form-data/{objectType}/{objectId}/{formConfigId}
 * Returns null if no data exists (204 response).
 */
export const getFormData = async (
  objectType: string,
  objectId: string,
  formConfigId: string,
): Promise<FormDataDto | null> => {
  const response = await api.get<FormDataDto>(
    `${basePath(objectType, objectId)}/${formConfigId}`,
    { validateStatus: (status) => status === 200 || status === 204 },
  );
  if (response.status === 204) return null;
  return response.data;
};

/**
 * List all form data for the specified scope.
 * GET /form-data/{objectType}/{objectId}
 */
export const listFormData = async (
  objectType: string,
  objectId: string,
  params: { status?: 'DRAFT' | 'SUBMITTED'; page?: number; size?: number; sort?: string } = {},
): Promise<PaginatedResponse<FormDataDto>> => {
  const response = await api.get<PaginatedResponse<FormDataDto>>(
    basePath(objectType, objectId),
    { params },
  );
  return response.data;
};

/**
 * Bulk fetch form data for multiple form configs in one call.
 * POST /form-data/{objectType}/{objectId}/bulk
 * Returns only records that exist; missing records are silently excluded.
 */
export const bulkGetFormData = async (
  objectType: string,
  objectId: string,
  payload: BulkFormDataPayload,
): Promise<FormDataDto[]> => {
  const response = await api.post<FormDataDto[]>(
    `${basePath(objectType, objectId)}/bulk`,
    payload,
  );
  return response.data;
};

/**
 * Get version history for a form data record.
 * GET /form-data/{objectType}/{objectId}/{formConfigId}/versions
 */
export const getFormDataVersions = async (
  objectType: string,
  objectId: string,
  formConfigId: string,
  params: { page?: number; size?: number; sort?: string } = {},
): Promise<PaginatedResponse<FormDataVersionDto>> => {
  const response = await api.get<PaginatedResponse<FormDataVersionDto>>(
    `${basePath(objectType, objectId)}/${formConfigId}/versions`,
    { params },
  );
  return response.data;
};
